// src/pages/NoticeListPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import "../styles/notice.css";
import {
  fetchNotices,
  fetchNoticeDetail,
  fetchNoticeFiles,
  fileDownloadUrl,
} from "../lib/notice";

// 날짜 포맷터
function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// 상세 본문 컴포넌트 (실제 데이터 렌더)
function NoticeDetailBody({ data, files }) {
  const type = data?.type || "";
  const title = data?.title || "";
  const content = data?.content || "";
  const date = data?.createdAt ? fmtDate(data.createdAt) : data?.date || "";

  return (
    <div className="nd-body">
      <p className="nd-meta">
        <span>분류: {type}</span>
        {date && <span>작성일: {date}</span>}
      </p>

      <h3 style={{ margin: "8px 0 12px" }}>{title}</h3>

      {/* 본문 (줄바꿈 유지) */}
      <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}>
        {content || "(내용이 없습니다.)"}
      </div>

      {/* 첨부파일 */}
      <div className="nd-files" style={{ marginTop: 16 }}>
        <h4 style={{ margin: "12px 0 8px" }}>첨부파일</h4>
        {files?.length ? (
          <ul className="nd-file-list">
            {files.map((f) => (
              <li key={f.id}>
                <a href={fileDownloadUrl(f.id)} download>{f.originalName}</a>
                {" "}
                <span className="meta">
                  {Number.isFinite(f.size) ? `${(f.size / 1024).toFixed(1)} KB` : ""}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <div>첨부 없음</div>
        )}
      </div>
    </div>
  );
}

export default function NoticeListPage() {
  // 🔗 리스트
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // 검색/필터 UI
  const [category, setCategory] = useState("ALL");
  const [field, setField] = useState("TITLE");
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  // 상세 상태
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [files, setFiles] = useState([]);

  const pageSize = 10;

  // ✅ 마운트: 공지 리스트 로드
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const list = await fetchNotices(); // size=1000 (거의 전체)
        if (!alive) return;
        setItems(list.sort((a, b) => (b.id ?? 0) - (a.id ?? 0)));
      } catch (e) {
        if (!alive) return;
        setErr(e.message || String(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // 🔍 검색/필터
  const data = useMemo(() => {
    let list = items;
    if (category !== "ALL") list = list.filter((it) => it.type === category);
    const q = query.trim();
    if (q) {
      const lower = q.toLowerCase();
      list = list.filter((it) => {
        if (field === "TITLE" || field === "TITLE_CONTENT") {
          return (it.title || "").toLowerCase().includes(lower);
        }
        return false;
      });
    }
    return list;
  }, [items, category, field, query]);

  const total = data.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageData = data.slice((page - 1) * pageSize, page * pageSize);

  const goPage = (p) => setPage(Math.min(Math.max(1, p), totalPages));

  const onSubmitSearch = (e) => {
    e.preventDefault();
    setQuery(input);
    setPage(1);
    // 상세 닫기
    setSelectedId(null);
    setDetail(null);
    setFiles([]);
  };

  const onOpenDetail = (id) => {
    // 상세는 아래 useEffect가 자동으로 로드
    setSelectedId(id);
    setDetail(null);
    setFiles([]);
  };

  // ✅ selectedId 바뀔 때마다 상세/파일 자동 로드
  useEffect(() => {
    if (!selectedId) return;
    let alive = true;
    (async () => {
      try {
        setDetailLoading(true);
        const [d, flist] = await Promise.all([
          fetchNoticeDetail(selectedId).catch(() => null),
          fetchNoticeFiles(selectedId).catch(() => []),
        ]);
        if (!alive) return;
        setDetail(d);
        setFiles(Array.isArray(flist) ? flist : []);
      } finally {
        if (alive) setDetailLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [selectedId]);

  const selectedItem = selectedId ? data.find((d) => d.id === selectedId) : null;
  const selectedIndex = selectedItem ? data.findIndex((d) => d.id === selectedId) : -1;
  const prevItem = selectedIndex > 0 ? data[selectedIndex - 1] : null;
  const nextItem = selectedIndex >= 0 && selectedIndex < data.length - 1 ? data[selectedIndex + 1] : null;
  const isDetail = !!selectedItem;

  return (
    <div className="notice-page">
      <section className="notice-header">
        <div className="container">
          <h2>공지 사항</h2>
          <p>다이빙이지의 공지 사항과 이벤트 소식을 알려 드립니다.</p>
        </div>
      </section>

      <section className="notice-section">
        <div className="container">
          {/* 에러/로딩 */}
          {err && <div className="error">불러오기 오류: {err}</div>}
          {loading && !isDetail && <div className="loading">불러오는 중…</div>}

          {/* ================= 리스트 ================= */}
          {!isDetail && !loading && (
            <>
              {/* 툴바 */}
              <div className="notice-toolbar">
                <div className="left">
                  <div className="count">총 <strong>{total}</strong>건</div>
                </div>

                <div className="right">
                  <label className="select">
                    <select
                      value={category}
                      onChange={(e) => { setCategory(e.target.value); setPage(1); setSelectedId(null); }}
                    >
                      <option value="ALL">전체</option>
                      <option value="NOTICE">공지</option>
                      <option value="EVENT">이벤트</option>
                    </select>
                    <span className="chev">▾</span>
                  </label>

                  <label className="select">
                    <select
                      value={field}
                      onChange={(e) => { setField(e.target.value); setPage(1); setSelectedId(null); }}
                    >
                      <option value="TITLE">제목</option>
                      <option value="TITLE_CONTENT">제목+내용</option>
                    </select>
                    <span className="chev">▾</span>
                  </label>

                  <form className="search" onSubmit={onSubmitSearch}>
                    <input
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder="검색어 입력"
                    />
                    <button aria-label="검색" type="submit">
                      <svg className="ic" viewBox="0 0 24 24" aria-hidden="true">
                        <circle cx="11" cy="11" r="6" fill="none" stroke="currentColor" strokeWidth="1.8"/>
                        <line x1="15.5" y1="15.5" x2="20" y2="20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                      </svg>
                    </button>
                  </form>
                </div>
              </div>

              {/* 리스트 테이블 */}
              <div className="notice-table">
                <div className="thead">
                  <div className="th no">번호</div>
                  <div className="th title">제목</div>
                  <div className="th file">첨부파일</div>
                  <div className="th date">작성일</div>
                </div>

                <div className="tbody">
                  {pageData.map((it) => (
                    <div
                      className="tr clickable"
                      key={it.id}
                      onClick={() => onOpenDetail(it.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpenDetail(it.id)}
                      aria-label={`${it.title} 상세 보기`}
                    >
                      <div className="td no">{it.id}</div>
                      <div className="td title">
                        <span className="mobile-prefix">[{it.type}] </span>
                        <span className={`badge ${it.type === "EVENT" ? "event" : "notice"}`}>
                          {it.type === "EVENT" ? "EVENT" : "NOTICE"}
                        </span>
                        {it.title}
                      </div>
                      <div className="td file">
                        {it.hasFile && (
                          <span className="file-ic" title="첨부 있음" aria-label="첨부 있음">
                            <svg className="ic" viewBox="0 0 24 24" aria-hidden="true">
                              <path d="M12 3v10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                              <path d="M8.5 9.5L12 13l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                              <rect x="4" y="15" width="16" height="5" rx="2.2" ry="2.2" fill="none" stroke="currentColor" strokeWidth="1.8"/>
                            </svg>
                          </span>
                        )}
                      </div>
                      <div className="td date">{it.date}</div>
                    </div>
                  ))}

                  {pageData.length === 0 && (
                    <div className="tr empty">
                      <div className="td" style={{ gridColumn: "1 / -1" }}>검색 결과가 없습니다.</div>
                    </div>
                  )}
                </div>
              </div>

              {/* 페이징 */}
              <div className="notice-paging">
                <button className="nav" onClick={() => goPage(page - 1)} disabled={page === 1}>〈</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    className={`num ${n === page ? "active" : ""}`}
                    onClick={() => goPage(n)}
                  >
                    {n}
                  </button>
                ))}
                <button className="nav" onClick={() => goPage(page + 1)} disabled={page === totalPages}>〉</button>
              </div>
            </>
          )}

          {/* ================= 상세 ================= */}
          {isDetail && (
            <>
              <article className="notice-detail">
                <header className="nd-header">
                  <h3>[{selectedItem.type}] {selectedItem.title}</h3>
                  <div className="nd-line" />
                </header>

                {detailLoading && <div className="loading">상세 불러오는 중…</div>}
                {!detailLoading && (
                  <NoticeDetailBody data={detail || selectedItem} files={files} />
                )}

                <div className="nd-actions">
                  <button
                    type="button"
                    className="btn-back"
                    onClick={() => { setSelectedId(null); setDetail(null); setFiles([]); }}
                  >
                    목록
                  </button>
                </div>
              </article>

              {/* 이전/다음 */}
              <div className="nd-nav">
                <button
                  type="button"
                  className="nd-nav-item"
                  disabled={!prevItem}
                  onClick={() => { if (prevItem) { setDetail(null); setFiles([]); setSelectedId(prevItem.id); } }}
                >
                  <svg className="nd-chevron" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M6 14l6-6 6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className="label">이전</span>
                  <span className="title">{prevItem ? `[${prevItem.type}] ${prevItem.title}` : "이전 글이 없습니다."}</span>
                </button>

                <button
                  type="button"
                  className="nd-nav-item"
                  disabled={!nextItem}
                  onClick={() => { if (nextItem) { setDetail(null); setFiles([]); setSelectedId(nextItem.id); } }}
                >
                  <svg className="nd-chevron" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M6 10l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className="label">다음</span>
                  <span className="title">{nextItem ? `[${nextItem.type}] ${nextItem.title}` : "다음 글이 없습니다."}</span>
                </button>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
